import { SnapshotCameraPosition } from './snapshotImage';
import { z } from 'zod';

/**
 * One cart camera image as published to consumers outside the platform: over SignalR to the session monitor
 * (`storeSessionSnapshotReceived`), and over the event hub from store-processing to store-manager.
 *
 * Mirrors the .NET `WhizCart.DotnetShared.CartImages.CartImageView` — a hand-written mirror, there is no codegen,
 * so any field added on that side has to be added here too.
 *
 * The image URL arrives already resolved as {@link url}: v1 stored a `host` and a `relativePath`, v2 stores
 * neither and derives both from the logical path, and the producer collapses that difference so no consumer has
 * to branch on the storage generation.
 */
export const CartImageView = z.object({
    sessionGuid: z.string(),
    correlationGuid: z.string().nullish(),
    logicalPath: z.string(),
    url: z.string().nullish(),
    camera: SnapshotCameraPosition,
    trigger: z.string().nullish(),
    index: z.number().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
    timestamp: z.coerce.date().nullish(),

    /**
     * @deprecated Only sent by the pre-#42118 producer, which published the full `CartCameraImageStorageModel`.
     * Kept so a message already in flight during the .NET rollout still resolves a URL; remove once that version
     * is deployed everywhere. Use {@link cartImageViewUrl} rather than reading these.
     */
    host: z.string().nullish(),
    /** @deprecated See {@link host}. */
    relativePath: z.string().nullish(),
});
export type CartImageView = z.infer<typeof CartImageView>;

/**
 * Resolves the displayable image URL of a notification, tolerating both producer generations.
 *
 * @returns The image URL, or `undefined` when the payload carries neither a resolved `url` nor a
 * `host`/`relativePath` pair — which is what an unconfigured `CartImages:BaseUrl` on the producer looks like from
 * here. Callers should drop such an image rather than render a broken one.
 */
export function cartImageViewUrl(image: CartImageView): string | undefined {
    if (image.url) {
        return image.url;
    }

    // A half-populated pair cannot produce a working URL — a bare host, or a bare
    // container-relative path — and everything returned here is treated as displayable.
    if (image.host && image.relativePath) {
        return `${image.host}${image.relativePath}`;
    }

    return undefined;
}
